import type { Vec2 } from "./types";
import { polygonsOverlap } from "./geom";

/**
 * Door-clearance geometry.
 *
 * For every door opening we build a "no furniture" rectangle in room-coords
 * that spans the opening along the wall and extends inward (perpendicular
 * to the wall, into the room) by exactly the door's swing radius — which,
 * for a standard hinged leaf, equals the door's width.
 *
 * This rectangle is a strict superset of the swing quarter-disk described
 * in the AI prompt: the disk is centered at one corner of the rectangle
 * with radius equal to the rectangle's perpendicular extent, so it sits
 * entirely inside the rectangle. The rest of the rectangle is the
 * threshold a person walks through — also off-limits. Treating the
 * union as a single oriented rectangle keeps the runtime check trivially
 * cheap (one SAT pass) and is easier to reason about than testing a disk
 * sector and a segment strip separately.
 *
 * On top of body-overlap we also check that drawer/door fronts have a
 * usable clearance strip that doesn't intrude into a door swing zone —
 * see {@link furnitureFrontBlocksDoor}. The shape is parameterized by
 * `frontKind` (from the catalog) so symmetric pieces are exempt.
 *
 * A wall cutout (`style: "cutout"`) has no leaf, and a sliding door's panel
 * runs along the wall ({@link isSlidingDoor}). Their no-furniture rectangle
 * is only the passage mouth ({@link CUTOUT_CLEARANCE_IN} deep), not a swing
 * radius equal to the opening width.
 *
 * The mirror at functions/src/doorClearance.ts must stay in sync.
 */

/** Inches of floor kept clear in front of a door with no leaf swinging into
 *  the room (a wall cutout or a sliding door): the passage mouth. */
export const CUTOUT_CLEARANCE_IN = 12;

/** A design stores no "sliding" flag, so the catalog name says it ("Sliding
 *  door", "Patio door", "Pocket door"). Gate and cutout names win, in the
 *  same order the 3D render picks a door's construction. */
const SLIDING_DOOR_NAME = /slid|patio|glider|pocket/i;
const NOT_SLIDING_NAME = /\bgate\b|cut ?out|opening|archway|pass-?through/i;

/** True for a door whose panel slides along the wall instead of swinging. */
export function isSlidingDoor(o: { kind: string; name?: string; style?: string }): boolean {
  if (o.kind !== "door" || o.style) return false;
  const name = o.name ?? "";
  return SLIDING_DOOR_NAME.test(name) && !NOT_SLIDING_NAME.test(name);
}

/** Default clearance (inches) extended forward from a piece's front edge
 *  when checking front-into-door-swing collisions. 24" matches the
 *  "personal space around seats" guideline already cited in the prompt
 *  and is a typical drawer/door extension distance. */
export const DEFAULT_FRONT_CLEARANCE_IN = 24;

/** Tag for the kind of front a piece presents. Mirrors `FrontKind` in
 *  src/lib/catalog.ts; redefined locally so this helper has no upward
 *  dependency on the catalog module. */
export type DoorClearanceFrontKind =
  | "none"
  | "seat"
  | "sleep"
  | "drawer"
  | "door"
  | "screen"
  | "shelf";

/** Front-kinds whose front edge needs clearance to actually be used (you
 *  pull a drawer or swing a door outward). Other oriented kinds (seat,
 *  sleep, screen, shelf) are not blocked even if their front faces a door
 *  zone — they don't have a hinged or sliding element that would collide. */
export function frontKindNeedsClearance(kind: DoorClearanceFrontKind): boolean {
  return kind === "drawer" || kind === "door";
}

/** Subset of `Opening` needed for the door-clearance check. Defined
 *  locally so the helper has no dependency on the wider opening shape
 *  (and so the server mirror can use the exact same surface). */
export interface DoorClearanceOpening {
  kind: "door" | "window";
  /** Catalog name; a sliding door keeps a shallow passage ({@link isSlidingDoor}). */
  name?: string;
  wallIndex: number;
  alongWall: number;
  width: number;
  /** "cutout" keeps a shallow passage instead of a full swing radius. */
  style?: "cutout";
  /** When set, the door sits on this segment instead of `polygon[wallIndex]`.
   *  Used for interior walls. The polygon is still used to pick a swing side. */
  segment?: { a: Vec2; b: Vec2 };
}

/** Compute the four corners (room coords) of a door's no-furniture
 *  rectangle. Returns null when the input isn't a door, the polygon is
 *  degenerate, the wall index is out of range, or the inward direction
 *  can't be resolved (which shouldn't happen for any well-formed room). */
export function doorClearanceRect(
  opening: DoorClearanceOpening,
  polygon: Vec2[]
): Vec2[] | null {
  if (opening.kind !== "door") return null;
  const n = polygon.length;
  if (n < 3) return null;
  let a: Vec2;
  let b: Vec2;
  if (opening.segment) {
    a = opening.segment.a;
    b = opening.segment.b;
  } else {
    if (opening.wallIndex < 0 || opening.wallIndex >= n) return null;
    a = polygon[opening.wallIndex];
    b = polygon[(opening.wallIndex + 1) % n];
  }
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const wallLen = Math.hypot(dx, dy);
  if (wallLen === 0) return null;
  const ux = dx / wallLen;
  const uy = dy / wallLen;
  const halfW = opening.width / 2;
  const startAlong = Math.max(0, opening.alongWall - halfW);
  const endAlong = Math.min(wallLen, opening.alongWall + halfW);
  const segStart: Vec2 = {
    x: a.x + ux * startAlong,
    y: a.y + uy * startAlong,
  };
  const segEnd: Vec2 = { x: a.x + ux * endAlong, y: a.y + uy * endAlong };
  const inward = inwardNormalFor(segStart, segEnd, ux, uy, polygon);
  if (!inward) return null;
  // Hinged doors: depth = swing radius = door width, so the rectangle
  // contains the swing quarter-disk pinned to the hinge corner. A wall
  // cutout or a sliding door has nothing swinging in, so only the passage
  // mouth is off-limits.
  const depth =
    opening.style === "cutout" || isSlidingDoor(opening) ? CUTOUT_CLEARANCE_IN : opening.width;
  return [
    segStart,
    segEnd,
    { x: segEnd.x + inward.x * depth, y: segEnd.y + inward.y * depth },
    { x: segStart.x + inward.x * depth, y: segStart.y + inward.y * depth },
  ];
}

/** True iff a (possibly rotated) furniture footprint overlaps the door's
 *  no-furniture rectangle. Pass in the furniture's world-space footprint
 *  outline — either the four corners from `furnitureCorners` in geom.ts or
 *  a true polygon (L / U sectionals, round tables) from the resolver.
 *  Convex outlines take the SAT fast path; concave ones fall back to an
 *  exact clipping test. Touching edges count as not overlapping, so a
 *  piece flush against the threshold boundary passes. */
export function furnitureBlocksDoor(
  furnitureOutline: Vec2[],
  opening: DoorClearanceOpening,
  polygon: Vec2[]
): boolean {
  const door = doorClearanceRect(opening, polygon);
  if (!door) return false;
  if (furnitureOutline.length <= 4 || isConvexOutline(furnitureOutline)) {
    return orientedRectsOverlap(furnitureOutline, door);
  }
  return polygonsOverlap(furnitureOutline, door);
}

function isConvexOutline(pts: Vec2[]): boolean {
  const n = pts.length;
  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const c = pts[(i + 2) % n];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) continue;
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/** Compute the rectangular "front-clearance" strip extending outward from
 *  a piece's front edge (its local +y edge after rotation) by
 *  `clearanceIn` inches. Returns the four corners in world space, or null
 *  for symmetric pieces (`frontKind === "none"`) where the strip has no
 *  meaning. The strip is `width` long (along the front edge) by
 *  `clearanceIn` deep (outward from the front). */
export function furnitureFrontClearanceRect(
  cx: number,
  cy: number,
  width: number,
  depth: number,
  rotationDeg: number,
  frontKind: DoorClearanceFrontKind,
  clearanceIn: number = DEFAULT_FRONT_CLEARANCE_IN
): Vec2[] | null {
  if (frontKind === "none") return null;
  if (clearanceIn <= 0) return null;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const hw = width / 2;
  const hd = depth / 2;
  // Local coords (pre-rotation): front-edge endpoints at y=+hd, strip
  // extends to y = +hd + clearanceIn.
  const local: Vec2[] = [
    { x: -hw, y: hd },
    { x: hw, y: hd },
    { x: hw, y: hd + clearanceIn },
    { x: -hw, y: hd + clearanceIn },
  ];
  return local.map((p) => ({
    x: cx + p.x * cos - p.y * sin,
    y: cy + p.x * sin + p.y * cos,
  }));
}

/** True iff a drawer/door piece's front-clearance strip overlaps the
 *  door's no-furniture rectangle. Returns false for symmetric pieces
 *  (`frontKind === "none"`) and for kinds that don't need clearance
 *  (seat / sleep / screen / shelf) — sofas and beds can have their
 *  fronts pointed at a door without functional failure; we leave that
 *  to the model's softer feng-shui rationale. */
export function furnitureFrontBlocksDoor(
  cx: number,
  cy: number,
  width: number,
  depth: number,
  rotationDeg: number,
  frontKind: DoorClearanceFrontKind,
  opening: DoorClearanceOpening,
  polygon: Vec2[],
  clearanceIn: number = DEFAULT_FRONT_CLEARANCE_IN
): boolean {
  if (!frontKindNeedsClearance(frontKind)) return false;
  const strip = furnitureFrontClearanceRect(
    cx,
    cy,
    width,
    depth,
    rotationDeg,
    frontKind,
    clearanceIn
  );
  if (!strip) return false;
  const door = doorClearanceRect(opening, polygon);
  if (!door) return false;
  return orientedRectsOverlap(strip, door);
}

/** Separating Axis Theorem for two convex polygons. Strict: if
 *  the projections share only a single point we treat the rectangles
 *  as separated (no overlap). */
function orientedRectsOverlap(a: Vec2[], b: Vec2[]): boolean {
  const axes = edgeNormals(a).concat(edgeNormals(b));
  for (const axis of axes) {
    const [aMin, aMax] = projectPoly(a, axis);
    const [bMin, bMax] = projectPoly(b, axis);
    if (aMax <= bMin) return false;
    if (bMax <= aMin) return false;
  }
  return true;
}

function edgeNormals(corners: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    // Perpendicular to the edge. No need to normalize for SAT.
    out.push({ x: -(b.y - a.y), y: b.x - a.x });
  }
  return out;
}

function projectPoly(corners: Vec2[], axis: Vec2): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (const c of corners) {
    const p = c.x * axis.x + c.y * axis.y;
    if (p < min) min = p;
    if (p > max) max = p;
  }
  return [min, max];
}

/** Pick the perpendicular to (ux, uy) that points into the polygon
 *  interior. We probe by stepping a small distance from the segment's
 *  midpoint along each candidate normal; whichever probe lands inside
 *  the polygon is the inward direction. Robust to either polygon
 *  winding, which the rest of the codebase doesn't enforce. */
function inwardNormalFor(
  segStart: Vec2,
  segEnd: Vec2,
  ux: number,
  uy: number,
  polygon: Vec2[]
): Vec2 | null {
  const mid: Vec2 = {
    x: (segStart.x + segEnd.x) / 2,
    y: (segStart.y + segEnd.y) / 2,
  };
  const candidates: Vec2[] = [
    { x: -uy, y: ux },
    { x: uy, y: -ux },
  ];
  // 1" probe is small enough to stay inside an L-shape pocket but large
  // enough to be unambiguous about which side of the wall is interior.
  const PROBE = 1;
  for (const cand of candidates) {
    const probe = {
      x: mid.x + cand.x * PROBE,
      y: mid.y + cand.y * PROBE,
    };
    if (pointInPolygon(probe, polygon)) return cand;
  }
  return null;
}

function pointInPolygon(point: Vec2, polygon: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
